import React, { useEffect, useState } from 'react';
import { getOrgAnalytics, getOrgAnalyticsDetails, getOrgAnalyticsUsers, getOrgAnalyticsUserDetails } from '../../api/analytics';
import { getToday, getPresetDates } from '../../utils/analyticsHelpers';
import '../../components/analytics/sections/analyticsDashboard.css';
import DateFilterBar from '../../components/analytics/sections/DateFilterBar';
import OverviewCards from '../../components/analytics/sections/OverviewCards';
import DailyActivity from '../../components/analytics/sections/DailyActivity';
import BookingFunnel from '../../components/analytics/sections/BookingFunnel';
import PaymentOverview from '../../components/analytics/sections/PaymentOverview';
import Conversion from '../../components/analytics/sections/Conversion';
import MostViewedTrips from '../../components/analytics/MostViewedTrips';
import UsersVisitors from '../../components/analytics/usersVisitors/UsersVisitors';

// Keep in sync with the page size UsersVisitors/Pagination use - it's the
// "first page" fetched here and handed down as a prop so that component
// doesn't have to re-fetch the same data on mount.
const USERS_PAGE_SIZE = 20;

const EMPTY_USERS_DATA = {
    summary: {
        uniqueUsers: 0,
        loggedInUsers: 0,
        anonymousVisitors: 0,
        totalVisitors: 0
    },
    users: []
};

// Organizer-facing analytics. Same dashboard building blocks as the
// support-admin version, but every number here comes from the org-scoped
// backend endpoints (/org/trip/analytics/...), which only ever look at
// events tied to THIS organizer's own trips. "Search Insights" is left
// out on purpose - search events happen before a trip is picked, so they
// can't be attributed to one organizer.
const OrgAnalytics = () => {
    const today = getToday();

    const [datePreset, setDatePreset] = useState('today');

    const [from, setFrom] = useState(today);
    const [to, setTo] = useState(today);

    const [analytics, setAnalytics] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [details, setDetails] = useState({ topTrips: [] });
    const [usersData, setUsersData] = useState(EMPTY_USERS_DATA);

    const { users = 0, sessions = 0, tripViews = 0, funnel = {}, daily = [] } = analytics;

    // -----------------------------------------
    // Fetch overview numbers
    // -----------------------------------------

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await getOrgAnalytics({ from, to });

            const responseBody = response?.data ?? response ?? null;

            if (!responseBody) {
                throw new Error('Analytics response is empty');
            }

            const analyticsData = responseBody?.data ?? responseBody;

            setAnalytics(analyticsData || {});
        } catch (err) {
            console.error('Org analytics fetch error:', err);

            setError(err?.response?.data?.message || err?.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    // -----------------------------------------
    // Fetch most viewed trips
    // -----------------------------------------

    const fetchAnalyticsDetails = async () => {
        try {
            const response = await getOrgAnalyticsDetails({ from, to });

            const responseBody = response?.data ?? response ?? null;

            if (!responseBody) {
                throw new Error('Analytics details response is empty');
            }

            const detailsData = responseBody?.data ?? responseBody;

            setDetails({
                topTrips: detailsData?.topTrips || []
            });
        } catch (err) {
            console.error('Org analytics details fetch error:', err);

            setDetails({ topTrips: [] });
        }
    };

    // -----------------------------------------
    // Fetch the first page of users/visitors who interacted
    // with this organizer's trips
    // -----------------------------------------

    const fetchAnalyticsUsers = async () => {
        try {
            const response = await getOrgAnalyticsUsers({
                from,
                to,
                limit: USERS_PAGE_SIZE,
                offset: 0
            });

            const responseBody = response?.data;

            if (!responseBody?.status) {
                throw new Error(responseBody?.message || 'Failed to fetch users analytics');
            }

            setUsersData(responseBody.data);
        } catch (error) {
            console.error('Failed to fetch org users analytics:', error);

            setUsersData(EMPTY_USERS_DATA);
        }
    };

    useEffect(() => {
        if (!from || !to) {
            return;
        }

        fetchAnalytics();
        fetchAnalyticsDetails();
        fetchAnalyticsUsers();
    }, [from, to]);

    const handleDatePreset = (preset) => {
        if (preset === 'custom') {
            setDatePreset('custom');
            return;
        }

        const dates = getPresetDates(preset);

        if (!dates) {
            return;
        }

        setDatePreset(preset);
        setFrom(dates.from);
        setTo(dates.to);
    };

    const handleFromChange = (event) => {
        const value = event.target.value;

        setDatePreset('custom');
        setFrom(value);

        if (value > to) {
            setTo(value);
        }
    };

    const handleToChange = (event) => {
        const value = event.target.value;

        setDatePreset('custom');
        setTo(value);
    };

    return (
        <div className='analyticsPage'>
            {/* ------------------------------------------------ */}
            {/* Header */}
            {/* ------------------------------------------------ */}

            <div className='analyticsHeader'>
                <div>
                    <h1>Analytics</h1>

                    <p>Track visitors, bookings and payments for your trips</p>
                </div>

                <DateFilterBar
                    datePreset={datePreset}
                    from={from}
                    to={to}
                    today={today}
                    onPresetChange={handleDatePreset}
                    onFromChange={handleFromChange}
                    onToChange={handleToChange}
                />
            </div>

            {/* ------------------------------------------------ */}
            {/* Error / Loading */}
            {/* ------------------------------------------------ */}

            {error && <div className='analyticsError'>{error}</div>}

            {loading && <div className='analyticsLoading'>Loading analytics...</div>}

            {/* ------------------------------------------------ */}
            {/* Overview Cards */}
            {/* ------------------------------------------------ */}

            <OverviewCards
                cards={[
                    { label: 'Visitors', value: users },
                    { label: 'Sessions', value: sessions },
                    { label: 'Trip Views', value: tripViews }
                ]}
            />

            {/* ------------------------------------------------ */}
            {/* Dashboard Grid */}
            {/* ------------------------------------------------ */}

            <div className='analyticsDashboardGrid'>
                <DailyActivity daily={daily} />

                <BookingFunnel tripViews={tripViews} funnel={funnel} />

                <PaymentOverview
                    paymentInitiated={funnel.paymentInitiated}
                    paymentSuccess={funnel.paymentSuccess}
                    paymentFailed={funnel.paymentFailed}
                    bookingConfirmed={funnel.bookingConfirmed}
                />

                <Conversion tripViews={tripViews} funnel={funnel} />

                <MostViewedTrips trips={details.topTrips} />

                <UsersVisitors
                    data={usersData}
                    from={from}
                    to={to}
                    fetchUsers={getOrgAnalyticsUsers}
                    fetchUserDetails={getOrgAnalyticsUserDetails}
                />
            </div>
        </div>
    );
};

export default OrgAnalytics;
